/**
 * Post-OAuth provisioning. Mirrors SignUpPopup's email-signup side effects so Google/Apple
 * users get the same Firestore state, minus the email-only bits (no welcome/set-password
 * email, no signOut).
 *
 * - New users: create users/{uid} via the shared buildNewUserDoc factory, auto-queue .edu
 *   verification, and apply saved date-idea preferences.
 * - Any sign-in (new or returning): link an anonymous survey response captured this session.
 */
import { doc, setDoc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAdditionalUserInfo } from 'firebase/auth';
import { db } from '../firebase';
import { buildNewUserDoc } from './newUserDefaults';
import { tryFulfillDailyVibeEmailInvitesAfterAuth } from '../utils/fulfillDailyVibeEmailInvitesAfterAuth';
import { isEduEmail } from './berkeleyEmailAccess';
import {
  getPendingPosterReferralForSignup,
  clearPosterReferralFromSession,
} from './posterReferralLink';

/** Link an anonymous inline survey response (this session) to the signed-in user. */
async function linkSurveyResponse(uid) {
  let lastSurveyResponseId = null;
  try {
    lastSurveyResponseId = sessionStorage.getItem('lastSurveyResponseId');
  } catch (_) {}
  if (!lastSurveyResponseId) return;
  try {
    await updateDoc(doc(db, 'survey_responses', lastSurveyResponseId), { userId: uid });
    sessionStorage.removeItem('lastSurveyResponseId');
  } catch (err) {
    console.error('Error linking survey response to user:', err);
  }
}

/** Apply date-idea preferences saved in localStorage before signup. */
async function applySavedDateIdeaPreferences(uid) {
  let raw = null;
  try {
    raw = localStorage.getItem('dateIdeaPreferences');
  } catch (_) {}
  if (!raw) return;
  try {
    const prefs = JSON.parse(raw);
    await setDoc(doc(db, 'users', uid), { preferences: prefs }, { merge: true });
    localStorage.removeItem('dateIdeaPreferences');
  } catch (err) {
    console.error('Error saving preferences after OAuth signup:', err);
  }
}

/** Map a Firebase providerId to our signupSource tag. */
function signupSourceFromProviderId(providerId) {
  if (providerId === 'apple.com') return 'apple';
  if (providerId === 'google.com') return 'google';
  return 'oauth';
}

/**
 * @param {import('firebase/auth').UserCredential} result - from signInWithPopup/getRedirectResult
 * @param {object} [opts]
 * @param {'google'|'apple'} [opts.signupSource] - provider used; derived from the result when omitted
 * @returns {Promise<{ isNewUser: boolean }>}
 */
export async function provisionUserAfterAuth(result, { signupSource } = {}) {
  const user = result?.user;
  if (!user?.uid) return { isNewUser: false };

  const additionalInfo = getAdditionalUserInfo(result);
  const isNewUser = Boolean(additionalInfo?.isNewUser);
  const resolvedSignupSource = signupSource || signupSourceFromProviderId(additionalInfo?.providerId);
  const email = (user.email || '').trim().toLowerCase();
  const isEdu = isEduEmail(email);

  if (isNewUser) {
    const posterReferral = getPendingPosterReferralForSignup();
    const matchFriendReferralFromFeed =
      posterReferral && posterReferral.referrerUid !== user.uid
        ? {
            referrerUid: posterReferral.referrerUid,
            posterId: posterReferral.posterId,
            capturedAt: serverTimestamp(),
          }
        : null;

    try {
      /* merge: true so a FoxChat auto-save that landed first isn't wiped (that race
         dropped the collage row, and the deferred fox-mask job then no-op'd). */
      await setDoc(
        doc(db, 'users', user.uid),
        buildNewUserDoc({
          email: user.email,
          signupSource: resolvedSignupSource,
          isEdu,
          eduEmail: email,
          matchFriendReferralFromFeed,
        }),
        { merge: true },
      );
      if (matchFriendReferralFromFeed) clearPosterReferralFromSession();
      if (isEdu) {
        await addDoc(collection(db, 'berkeleyVerifications'), {
          userId: user.uid,
          accountEmail: email,
          berkeleyEmail: email,
          status: 'pending',
          submittedAt: serverTimestamp(),
          source: 'oauth_signup',
        });
      }
    } catch (err) {
      // Auth already succeeded; don't block sign-in if the Firestore write fails.
      console.error('Error provisioning OAuth user document:', err);
    }

    await applySavedDateIdeaPreferences(user.uid);
  }

  await linkSurveyResponse(user.uid);

  if (isNewUser) {
    await tryFulfillDailyVibeEmailInvitesAfterAuth();
  }

  return { isNewUser };
}
